/**
 * Interface for react-intl's IntlShape object
 */
interface IntlFormatMessage {
  formatMessage?: (descriptor: { id: string; defaultMessage: string }) => string;
}

/**
 * Interface for interval message configuration
 */
interface IntervalMessage {
  id: string;
  defaultMessage: string;
}

/**
 * Gets localized interval text with optional case formatting
 * @param intervalType - The interval type (e.g., 'daily', 'weekly', 'monthly')
 * @param intl - The intl object from react-intl
 * @param lowercase - Whether to return lowercase text (default: false)
 * @returns The localized interval text
 */
export const getLocalizedInterval = (
  intervalType: string | undefined,
  intl: IntlFormatMessage | undefined,
  lowercase: boolean = false
): string => {
  const normalizedInterval = intervalType?.toLowerCase() || 'monthly';

  const intervalMessages: Record<string, IntervalMessage> = {
    daily: { id: 'DAILY', defaultMessage: lowercase ? 'daily' : 'Daily' },
    weekly: { id: 'WEEKLY', defaultMessage: lowercase ? 'weekly' : 'Weekly' },
    monthly: { id: 'MONTHLY', defaultMessage: lowercase ? 'monthly' : 'Monthly' },
  };

  const message = intervalMessages[normalizedInterval];
  if (message) {
    // If intl is available, use it for localization
    if (intl && intl.formatMessage) {
      const localizedMessage = intl.formatMessage(message);
      return lowercase ? localizedMessage.toLowerCase() : localizedMessage;
    }
    // Fallback to default message when intl is not available
    return message.defaultMessage;
  }

  // Handle unknown intervals
  return lowercase
    ? normalizedInterval
    : normalizedInterval.charAt(0).toUpperCase() + normalizedInterval.slice(1);
};
